import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useCartStore } from '@/store/cartStore'
import Button from '@/components/common/Button'
import { formatCurrency } from '@/utils/helpers'
import toast from 'react-hot-toast'

const ACCESSORIES = [
  {
    id: 'halter',
    nameKey: 'holder',
    price: 5.00,
    image: '/assets/shop/License plate holder.webp',
    descriptionKey: 'holderDesc',
  },
  {
    id: 'warndreieck',
    nameKey: 'triangle',
    price: 10.00,
    image: '/assets/shop/Warning triangle.webp',
    descriptionKey: 'triangleDesc',
  },
  {
    id: 'verbandskasten',
    nameKey: 'firstAid',
    price: 10.00,
    image: '/assets/shop/First aid kit.webp',
    descriptionKey: 'firstAidDesc',
  },
  {
    id: '3in1-set',
    nameKey: 'comboSet',
    price: 20.00,
    image: '/assets/shop/3in1-set.webp',
    descriptionKey: 'comboSetDesc',
  },
]

interface AccessoriesSectionProps {
  variant?: 'grid' | 'carousel'
}

const AccessoriesSection = ({ variant = 'grid' }: AccessoriesSectionProps) => {
  const { t } = useTranslation()
  const { addItem } = useCartStore()

  const handleAddToCart = (accessory: typeof ACCESSORIES[number]) => {
    addItem({
      productId: `acc-${accessory.id}`,
      productName: t(`accessories.${accessory.nameKey}`),
      price: accessory.price,
      quantity: 1,
    })

    toast.success(t('toast.productAdded'))
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gradient mb-3 sm:mb-4">{t('shop.accessories.title')}</h2>
        <p className="text-base sm:text-lg text-gray-400">
          {t('shop.accessories.description')}
        </p>
      </motion.div>

      {variant === 'carousel' ? (
        <div className="relative w-full overflow-hidden pb-4 pt-2 group/carousel">
          {/* Gradient Edges */}
          <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-16 bg-gradient-to-r from-dark-950 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-16 bg-gradient-to-l from-dark-950 to-transparent z-10 pointer-events-none"></div>

          <div className="flex w-max animate-scroll-left group-hover/carousel:animation-pause gap-4">
            {/* Double the array for infinite scroll effect */}
            {[...ACCESSORIES, ...ACCESSORIES].map((accessory, index) => (
              <motion.div
                key={`${accessory.id}-${index}`}
                whileHover={{ y: -5, scale: 1.02 }}
                className="flex-none w-32 sm:w-40 xl:w-48 group"
              >
                <Link to={`/shop#accessories`} className="block relative w-full aspect-square rounded-2xl bg-dark-800 overflow-hidden shadow-inner border border-gray-700/50">
                  <img
                    src={accessory.image}
                    alt={t(`accessories.${accessory.nameKey}`)}
                    className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-md"
                  />
                  {/* Name Overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-8 pb-3">
                    <h3 className="text-xs sm:text-sm font-bold text-white text-center group-hover:text-primary-400 transition-colors drop-shadow-md">
                      {t(`accessories.${accessory.nameKey}`)}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {ACCESSORIES.map((accessory, index) => (
            <motion.div
              key={accessory.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="card-hover group"
            >
              <Link to={`/product/acc-${accessory.id}`} className="block">
                <div className="w-full aspect-square mb-4 sm:mb-6 rounded-2xl bg-dark-800 flex items-center justify-center p-4 overflow-hidden shadow-inner border border-gray-700/50">
                  <img
                    src={accessory.image}
                    alt={t(`accessories.${accessory.nameKey}`)}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300 drop-shadow-md"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-gray-100 group-hover:text-primary-400 transition-colors">
                  {t(`accessories.${accessory.nameKey}`)}
                </h3>
              </Link>
              <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 min-h-[3rem]">
                {t(`accessories.${accessory.descriptionKey}`)}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl sm:text-3xl font-bold text-primary-400">
                  {formatCurrency(accessory.price)}
                </span>
                <Button onClick={() => handleAddToCart(accessory)} size="md">
                  {t('shop.accessories.addToCart')}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AccessoriesSection
